import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Play, ArrowLeft, Trash2 } from 'lucide-react';
import { TaskAPI, RunAPI } from '../lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/Tabs';
import { Table, Thead, Tbody, Th, Tr, Td } from '../components/ui/Table';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { StatusBadge } from '../components/StatusBadge';
import { RunRow } from '../components/RunRow';
import { Skeleton } from '../components/ui/Skeleton';
import { TopBar } from '../components/TopBar';
import { useToast } from '../components/ui/Toast';
import { Dialog } from '../components/ui/Dialog';
import { createRunStream } from '../lib/ws';
import { formatDate } from '../lib/format';
import cronstrue from 'cronstrue';
import { TaskForm } from './NewTask';

export function TaskDetailPage() {
  const { taskId } = useParams({ strict: false });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [liveOutput, setLiveOutput] = useState<string | null>(null);
  const [activeRunId, setActiveRunId] = useState<number | null>(null);

  const { data: task, isLoading } = useQuery({
    queryKey: ['task', taskId],
    queryFn: () => TaskAPI.get(Number(taskId)),
  });

  const { data: runsData } = useQuery({
    queryKey: ['runs', { taskId }],
    queryFn: () => RunAPI.list({ taskId, limit: '50' }),
    refetchInterval: activeRunId ? 3000 : false,
  });

  const runs: any[] = runsData?.items ?? runsData ?? [];

  useEffect(() => {
    const running = runs.find((r: any) => r.status === 'RUNNING');
    if (running && running.id !== activeRunId) {
      setActiveRunId(running.id);
      setLiveOutput('');
      const close = createRunStream(
        running.id,
        (chunk) => setLiveOutput((prev) => (prev ?? '') + chunk),
        () => { setActiveRunId(null); qc.invalidateQueries({ queryKey: ['runs', { taskId }] }); }
      );
      return close;
    }
  }, [runs, activeRunId, taskId, qc]);

  const runMut = useMutation({
    mutationFn: () => TaskAPI.run(Number(taskId)),
    onSuccess: () => { toast('Задачу запущено', 'success'); qc.invalidateQueries({ queryKey: ['runs', { taskId }] }); },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const toggleMut = useMutation({
    mutationFn: () => task?.enabled ? TaskAPI.disable(Number(taskId)) : TaskAPI.enable(Number(taskId)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['task', taskId] }),
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const deleteMut = useMutation({
    mutationFn: () => TaskAPI.delete(Number(taskId)),
    onSuccess: () => { navigate({ to: '/tasks' }); },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  let humanSchedule = task?.schedule ?? '';
  try { humanSchedule = cronstrue.toString(task?.schedule ?? ''); } catch {}

  return (
    <div>
      <TopBar
        title={isLoading ? 'Завантаження...' : (task?.name ?? 'Задача')}
        actions={
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/tasks' })}>
              <ArrowLeft size={14} /> Назад
            </Button>
            <Button
              variant="success"
              size="sm"
              loading={runMut.isPending}
              onClick={() => runMut.mutate()}
            >
              <Play size={12} /> Запустити
            </Button>
            <Button variant="danger" size="sm" onClick={() => setDeleteOpen(true)}>
              <Trash2 size={14} />
            </Button>
          </div>
        }
      />

      <div className="p-6">
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-full" />
          </div>
        ) : task ? (
          <Tabs defaultValue="overview">
            <TabsList>
              <TabsTrigger value="overview">Огляд</TabsTrigger>
              <TabsTrigger value="runs">Запуски</TabsTrigger>
              <TabsTrigger value="edit">Редагувати</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-tg-text-secondary">Назва</span>
                    <span className="text-tg-text font-medium">{task.name}</span>
                  </div>
                  {task.description && (
                    <div className="text-sm">
                      <span className="text-tg-text-secondary">Опис:</span>
                      <p className="text-tg-text mt-1">{task.description}</p>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-tg-text-secondary">Тип</span>
                    <Badge variant="muted">{task.type}</Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-tg-text-secondary">Розклад</span>
                    <div className="text-right">
                      <div className="font-mono text-tg-accent text-xs">{task.schedule}</div>
                      <div className="text-tg-text-secondary text-xs">{humanSchedule}</div>
                    </div>
                  </div>
                  {task.command && (
                    <div className="text-sm">
                      <span className="text-tg-text-secondary">Команда:</span>
                      <pre className="text-xs text-tg-text mt-1 p-2 rounded" style={{ background: 'var(--tg-bg)' }}>{task.command}</pre>
                    </div>
                  )}
                  {task.url && (
                    <div className="flex justify-between text-sm">
                      <span className="text-tg-text-secondary">URL</span>
                      <a href={task.url} className="text-tg-accent text-xs">{task.url}</a>
                    </div>
                  )}
                  <div className="flex justify-between text-sm items-center">
                    <span className="text-tg-text-secondary">Увімкнено</span>
                    <button
                      onClick={() => toggleMut.mutate()}
                      className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
                      style={{ background: task.enabled ? 'var(--tg-success)' : 'var(--tg-border)' }}
                    >
                      <span className={`inline-block h-3 w-3 rounded-full bg-white transition-transform ${task.enabled ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </div>
              </div>

              {liveOutput !== null && (
                <div className="mt-4">
                  <div className="text-sm font-medium text-tg-warning mb-2">Виконується зараз...</div>
                  <pre
                    className="text-xs p-3 rounded overflow-auto max-h-64 text-tg-text"
                    style={{ background: 'var(--tg-bg)' }}
                  >
                    {liveOutput || 'Очікуємо вивід...'}
                  </pre>
                </div>
              )}
            </TabsContent>

            <TabsContent value="runs">
              {runs.length > 0 ? (
                <Table>
                  <Thead>
                    <Tr>
                      <Th>Старт</Th>
                      <Th>Тривалість</Th>
                      <Th>Статус</Th>
                      <Th></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {runs.map((run: any) => (
                      <RunRow key={run.id} run={run} />
                    ))}
                  </Tbody>
                </Table>
              ) : (
                <p className="text-sm text-tg-text-secondary py-4">Запуски відсутні</p>
              )}
            </TabsContent>

            <TabsContent value="edit">
              <TaskForm
                initialData={task}
                taskId={Number(taskId)}
                onSuccess={() => {
                  qc.invalidateQueries({ queryKey: ['task', taskId] });
                  toast('Задачу збережено', 'success');
                }}
              />
            </TabsContent>
          </Tabs>
        ) : (
          <p className="text-tg-text-secondary">Задачу не знайдено</p>
        )}
      </div>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Видалити задачу?">
        <p className="text-sm text-tg-text-secondary mb-4">
          Задачу <strong className="text-tg-text">{task?.name}</strong> буде видалено безповоротно.
        </p>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => setDeleteOpen(false)}>Скасувати</Button>
          <Button variant="danger" loading={deleteMut.isPending} onClick={() => deleteMut.mutate()}>
            Видалити
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
